import { Search } from "@mui/icons-material";
import { Box, MenuItem, SvgIcon, Typography } from "@mui/material";
import { Form, Formik, FormikHelpers } from "formik";
import _ from "lodash";
import { useSnackbar } from "notistack";
import { useMemo, useCallback, ReactElement } from "react";

import {
  CampaignSampleContact,
  SetCampaignSampleContactMutation,
  SetCampaignSampleContactMutationVariables,
} from "~frontend/__generated__/graphql";
import RouteLeavingGuard from "~frontend/components/RouteLeavingGuard";
import { FormikSelect } from "~frontend/components/forms/FormSelect";
import { SET_CAMPAIGN_SAMPLE_CONTACT } from "~frontend/graphql/campaigns";
import theme from "~frontend/theme";
import { formatContactData } from "~frontend/util/contactFields";
import { prettyPhoneNumber } from "~frontend/util/phoneNumber";
import useMutation from "~frontend/util/useMutation";

import { FormikSaveButtons } from "../campaignEditorUtils/SaveButtons";
import { CampaignForEditor } from "../types";

type ContactMappingFormValues = {
  sampleContactId?: string | null;
  goToNext?: boolean;
};

export const SampleContactForm = ({
  campaign,
  goToNextSection,
}: {
  campaign: CampaignForEditor;
  goToNextSection: (opts?: { bypassRouteLeavingGuard?: boolean }) => void;
}): ReactElement => {
  const { enqueueSnackbar } = useSnackbar();
  const [editCampaignSampleContact] = useMutation<
    SetCampaignSampleContactMutation,
    SetCampaignSampleContactMutationVariables
  >(SET_CAMPAIGN_SAMPLE_CONTACT, {
    onErrorSnackbarMessage: "Could not edit sample contact",
  });
  const handleSave = async (
    { sampleContactId, goToNext }: ContactMappingFormValues,
    {
      resetForm,
      setSubmitting,
      setErrors,
    }: FormikHelpers<ContactMappingFormValues>
  ) => {
    const { errors } = await editCampaignSampleContact({
      variables: {
        input: {
          campaignId: campaign.id,
          contactId: sampleContactId ?? null,
        },
      },
    });

    setSubmitting(false);

    if (errors) {
      setErrors({ sampleContactId: errors.join(", ") });
    } else {
      resetForm({ values: { sampleContactId } });
      if (goToNext) {
        goToNextSection({ bypassRouteLeavingGuard: true });
      } else {
        enqueueSnackbar("Saved sample contact", { variant: "info" });
      }
    }
  };

  const availableContactsById = useMemo(
    () => _.keyBy(campaign.availableSampleContacts, "id"),
    [campaign.availableSampleContacts]
  );

  const renderContactDisplay = useCallback(
    (contact?: CampaignSampleContact) => {
      if (!contact) {
        return <>None</>;
      }

      const { firstName, lastName } = formatContactData({
        contactData: JSON.parse(contact.data ?? "{}"),
        campaignContactFields: campaign.contactFields,
      });

      return (
        <>
          {firstName || "(no first name)"} {lastName || "(no last name)"}{" "}
          {prettyPhoneNumber(contact.phoneNumber)}
        </>
      );
    },
    [campaign.contactFields]
  );

  return (
    <>
      <Typography variant="body1">
        Choose a contact from your list to use as a sample contact. This
        contact's information will be used for all message previews and
        character count estimations.
      </Typography>

      <Formik
        initialValues={
          {
            sampleContactId: campaign.sampleContact?.id,
          } as ContactMappingFormValues
        }
        onSubmit={handleSave}
      >
        {({ dirty }) => {
          return (
            <Form>
              <RouteLeavingGuard when={dirty} />

              <FormikSelect
                withNoneOption
                label="Sample Contact"
                fullWidth
                name="sampleContactId"
                InputProps={{ style: { marginBottom: theme.spacing(1) } }}
                renderValue={value => {
                  return (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: theme.spacing(1),
                      }}
                    >
                      <SvgIcon>
                        <Search />
                      </SvgIcon>
                      {renderContactDisplay(
                        availableContactsById[value as string]
                      )}
                    </Box>
                  );
                }}
              >
                {(campaign.availableSampleContacts || []).map(contact => (
                  <MenuItem key={contact.id} value={contact.id}>
                    {renderContactDisplay(contact)}
                  </MenuItem>
                ))}
              </FormikSelect>

              <FormikSaveButtons continueWithoutSaving={goToNextSection} />
            </Form>
          );
        }}
      </Formik>
    </>
  );
};
